<template>
  <VMenu
    v-model="isOpen"
    :activator="props.activator"
    :close-on-content-click="false"
    :location="props.location"
    content-class="border-thin !rounded-2xl"
    offset="20"
  >
    <VDatePicker
      :model-value="displayDates"
      :max="new Date()"
      :first-day-of-week="1"
      :weekday-format="'short'"
      :show-adjacent-months="true"
      hide-header
      class="range-picker"
    >
      <template #controls="{ disabled, nextMonth, prevMonth, monthYearText }">
        <div class="range-picker__controls">
          <div class="range-picker__month-nav">
            <VBtn
              :disabled="disabled.includes('prev-month')"
              color="primary"
              icon="$prev"
              @click="prevMonth"
            />

            <p>{{ monthYearText }}</p>

            <VBtn
              :disabled="disabled.includes('next-month')"
              color="primary"
              icon="$next"
              @click="nextMonth"
            />
          </div>

          <div
            v-if="props.showSelectedDate && displayDates.length"
            class="range-picker__selected-indicator"
            data-testid="selected-date-indicator"
          >
            <div class="range-picker__selected-box">{{ startDateText }}</div>
            <span>–</span>
            <div
              class="range-picker__selected-box"
              :class="{ 'range-picker__selected-box--empty': !hasEndDate }"
            >
              {{ endDateText }}
            </div>
          </div>
        </div>
      </template>

      <template #day="{ item, props: dayProps }">
        <div data-testid="selected-day" :class="dayContainerClass(item)">
          <VBtn
            v-bind="getDayButtonProps(dayProps)"
            :text="item.localized"
            variant="text"
            rounded="full"
            :class="dayButtonClass(item)"
            @click.stop="onDayClick(item.date)"
          />
        </div>
      </template>

      <template v-if="props.showRemoveButton" #actions>
        <div class="range-picker__actions">
          <DividerLine class="w-full" />

          <div class="range-picker__remove-wrap">
            <VBtn
              data-testid="remove-selection-button"
              class="font-semibold w-full border-gray-300 text-gray-700"
              :text="t('Remove dates')"
              variant="outlined"
              @click="onRemoveSelection"
            />
          </div>
        </div>
      </template>
    </VDatePicker>
  </VMenu>
</template>

<script setup lang="ts">
import type { Anchor } from 'vuetify/framework'
import { computed, ref, watch } from 'vue'
import { useFormatDate } from '@/composables/useFormatDate'
import DividerLine from '@/atoms/DividerLine.vue'
import { useI18n } from 'vue-i18n'

interface Props {
  selectedDates: Date[]
  showSelectedDate?: boolean
  showRemoveButton?: boolean
  activator?: HTMLElement
  location?: Anchor
}

interface Emits {
  (e: 'range-update', dates: Date[]): void

  (e: 'preview-update', dates: Date[]): void

  (e: 'remove-selection'): void
}

type DaySlotItem = {
  date: Date
  localized: string
  isToday?: boolean
  isWeekStart?: boolean
  isWeekEnd?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showSelectedDate: false,
  showRemoveButton: false,
  location: 'bottom center'
})

const emit = defineEmits<Emits>()
const { formatDateOnly } = useFormatDate()
const { t } = useI18n()

const isOpen = defineModel<boolean>({ default: false })
const pendingStartDate = ref<Date | null>(null)
const displayDates = ref<Date[]>([])

const ignoreNextExternalSync = ref(false)

watch(
  () => props.selectedDates,
  (value) => {
    if (ignoreNextExternalSync.value) {
      ignoreNextExternalSync.value = false
      return
    }

    displayDates.value = value.map((date) => new Date(date))
    pendingStartDate.value = value.length === 1 ? new Date(value[0]) : null
  },
  { immediate: true }
)

watch(isOpen, (open) => {
  if (!open) {
    ignoreNextExternalSync.value = false
    displayDates.value = props.selectedDates.map((date) => new Date(date))
    pendingStartDate.value =
      props.selectedDates.length === 1 ? new Date(props.selectedDates[0]) : null
  }
})

function getDayButtonProps(dayProps: Record<string, unknown> & { onClick?: () => void }) {
  const { onClick: _onClick, ...rest } = dayProps
  return rest
}

const normalizedDates = computed<number[]>(() => {
  return displayDates.value.map((date) => normalizeDate(date))
})

const first = computed<number | null>(() => {
  const values = normalizedDates.value
  return values.length ? Math.min(...values) : null
})

const last = computed<number | null>(() => {
  const values = normalizedDates.value
  return values.length ? Math.max(...values) : null
})

const isSingleDaySelection = computed(() => {
  return (
    displayDates.value.length > 0 &&
    first.value !== null &&
    last.value !== null &&
    first.value === last.value
  )
})

function onDayClick(clickedDate: Date) {
  const clicked = new Date(clickedDate)

  if (!pendingStartDate.value && displayDates.value.length >= 2) {
    pendingStartDate.value = clicked
    displayDates.value = [clicked]
    emit('preview-update', [clicked])
    return
  }

  if (!pendingStartDate.value) {
    pendingStartDate.value = clicked
    displayDates.value = [clicked]
    emit('preview-update', [clicked])
    return
  }

  const start = new Date(pendingStartDate.value)

  if (isSameCalendarDay(start, clicked)) {
    const from = startOfDay(start)
    const to = endOfDay(start)

    displayDates.value = [from, to]
    pendingStartDate.value = null
    emit('preview-update', [from, to])
    emit('range-update', [from, to])
    return
  }

  const [rawFrom, rawTo] = [start, clicked].sort((a, b) => a.getTime() - b.getTime())
  const from = startOfDay(rawFrom)
  const to = endOfDay(rawTo)

  displayDates.value = [from, to]
  pendingStartDate.value = null
  emit('preview-update', [from, to])
  emit('range-update', [from, to])
}

function onRemoveSelection() {
  ignoreNextExternalSync.value = false
  pendingStartDate.value = null
  displayDates.value = []
  emit('remove-selection')
}

function normalizeDate(date: Date): number {
  const copy = new Date(date)
  copy.setHours(0, 0, 0, 0)
  return copy.getTime()
}

function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function startOfDay(date: Date): Date {
  const copy = new Date(date)
  copy.setHours(0, 0, 0, 0)
  return copy
}

function endOfDay(date: Date): Date {
  const copy = new Date(date)
  copy.setHours(23, 59, 59, 999)
  return copy
}

function isPendingStart(date: Date): boolean {
  return !!pendingStartDate.value && normalizeDate(date) === normalizeDate(pendingStartDate.value)
}

function isRangeStart(date: Date): boolean {
  return first.value !== null && normalizeDate(date) === first.value
}

function isRangeEnd(date: Date): boolean {
  return last.value !== null && normalizeDate(date) === last.value
}

function isSelectedEdge(date: Date): boolean {
  return isRangeStart(date) || isRangeEnd(date)
}

function hasRangeBackground(date: Date): boolean {
  if (first.value === null || last.value === null) {
    return false
  }

  if (displayDates.value.length < 2) {
    return false
  }

  if (isSingleDaySelection.value) {
    return false
  }

  const normalized = normalizeDate(date)
  return normalized >= first.value && normalized <= last.value
}

const startDateText = computed(() => {
  return displayDates.value.length ? formatDateOnly(displayDates.value[0]) : ''
})

const endDateText = computed(() => {
  if (displayDates.value.length === 1) {
    return ''
  }

  return formatDateOnly(displayDates.value.at(-1) as Date)
})

const hasEndDate = computed(() => endDateText.value.length > 0)

function dayContainerClass(item: DaySlotItem) {
  return [
    'range-picker__day',
    {
      'range-picker__day--in-range': hasRangeBackground(item.date),
      'range-picker__day--range-start':
        displayDates.value.length > 1 && !isSingleDaySelection.value && isRangeStart(item.date),
      'range-picker__day--range-end':
        displayDates.value.length > 1 && !isSingleDaySelection.value && isRangeEnd(item.date)
    }
  ]
}

function isInRangeOnly(date: Date): boolean {
  return hasRangeBackground(date) && !isSelectedEdge(date)
}

function dayButtonClass(item: DaySlotItem) {
  const selected = isSelectedEdge(item.date)
  const pending = isPendingStart(item.date)
  const inRangeOnly = isInRangeOnly(item.date)
  const today = !!item.isToday && !selected && !pending

  return [
    'range-picker__day-btn',
    {
      'range-picker__day-btn--selected': selected,
      'range-picker__day-btn--pending': pending,
      'range-picker__day-btn--in-range': inRangeOnly,
      'range-picker__day-btn--today': today,
      'range-picker__day-btn--default': !selected && !pending && !today && !inRangeOnly
    }
  ]
}

defineExpose({
  onDayClick,
  onRemoveSelection
})
</script>

<style scoped>
:deep(.range-picker .v-picker__body) {
  flex-wrap: nowrap;
  padding: 20px 24px;
}

:deep(.range-picker .v-date-picker-controls) {
  padding: 0;
  height: unset;
}

:deep(.range-picker .v-date-picker-month) {
  padding: 0;
}

:deep(.range-picker .v-date-picker-month__days) {
  column-gap: 0;
  row-gap: 4px;
}

:deep(.v-picker__actions) {
  padding: 0;
}

.range-picker__controls {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 8px;
}

.range-picker__month-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.range-picker__selected-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding-bottom: 8px;
}

.range-picker__selected-box {
  flex: 1;
  min-width: 0;
  min-height: 42px;
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border: 1px solid rgb(209 213 219);
  border-radius: 8px;
  background: rgb(var(--v-theme-surface));
}

.range-picker__selected-box--empty:empty {
  display: flex;
}

.range-picker__actions {
  width: 100%;
}

.range-picker__remove-wrap {
  display: flex;
  justify-content: center;
  padding: 16px;
}

.range-picker__day {
  width: 100%;
  height: 100%;
  background: transparent;
}

.range-picker__day--in-range {
  background: rgb(229 231 235);
}

.range-picker__day--range-start {
  border-top-left-radius: 9999px;
  border-bottom-left-radius: 9999px;
}

.range-picker__day--range-end {
  border-top-right-radius: 9999px;
  border-bottom-right-radius: 9999px;
}

.range-picker__day-btn {
  width: 100%;
  height: 100%;
  min-width: 0;
  box-shadow: none;
  border: 1px solid transparent !important;
}

:deep(.range-picker__day-btn .v-btn__content) {
  font-weight: 400 !important;
}

:deep(.range-picker__day-btn .v-btn__overlay) {
  opacity: 0;
}

:deep(.range-picker__day-btn .v-btn__underlay) {
  display: none;
}

:deep(.range-picker__day--in-range .range-picker__day-btn .v-btn__content) {
  font-weight: 600 !important;
}

.range-picker__day-btn--selected {
  background: rgb(var(--v-theme-primary)) !important;
  color: rgb(var(--v-theme-surface)) !important;
  border-color: rgb(var(--v-theme-primary)) !important;
}

.range-picker__day-btn--pending {
  background: rgb(var(--v-theme-primary)) !important;
  color: rgb(var(--v-theme-surface)) !important;
  border-color: rgb(var(--v-theme-primary)) !important;
}

.range-picker__day-btn--today {
  background: transparent !important;
  color: rgb(var(--v-theme-primary)) !important;
  border-color: rgb(var(--v-theme-primary)) !important;
}

:deep(.range-picker__day-btn--selected .v-btn__content) {
  font-weight: 700 !important;
}

:deep(.range-picker__day-btn--today .v-btn__content) {
  font-weight: 700 !important;
}

.range-picker__day-btn--default {
  background: transparent !important;
  color: rgb(55 65 81) !important;
  border-color: transparent !important;
}

:deep(.range-picker__day-btn.v-btn--active::before),
:deep(.range-picker__day-btn.v-btn--active::after),
:deep(.range-picker__day-btn.v-btn--focused::before),
:deep(.range-picker__day-btn.v-btn--focused::after) {
  display: none !important;
}

:deep(.range-picker__day-btn:focus),
:deep(.range-picker__day-btn:focus-visible) {
  outline: none !important;
}
</style>
